import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AccrualReviewApproveResponse } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { TRANSACTION_DETAILS_QUERY_KEY } from "@components/transactions/TransactionDetails/useTransactionDetailsQuery";
import { getTransactionDetails } from "@lib/transactions";
import { AccrualMatchProvider } from "./AccrualMatchContext";
import { EditAllocationDrawer } from "./EditAllocationDrawer";
import { transactionToMatchableItem } from "./mappers";
import { MatchableItem } from "./types";

interface Props {
    transactionId: number;
    /**
     * The bill/invoice the user is splitting this tx's payment across; it
     * becomes the only entry in `EditAllocationDrawer`'s "Selected
     * matches" section.
     */
    documentSourceItem: MatchableItem;
    onClose: () => void;
    onSaveSuccess: (response: AccrualReviewApproveResponse) => void;
}

/**
 * Wraps `EditAllocationDrawer` in its own `AccrualMatchProvider` so the
 * reverse-match drawer can drill into a single selected transaction's
 * allocation without polluting the outer (doc-anchored) drawer's state.
 *
 * Lives in its own file because:
 * - it has its own data dependency (`getTransactionDetails`),
 * - it owns a separate provider scope,
 * - keeping `DocumentMatchDrawer.tsx` under the 200–300 line
 *   convention.
 */
export const DocumentEditAllocationContainer: React.FC<Props> = ({
    transactionId,
    documentSourceItem,
    onClose,
    onSaveSuccess,
}) => {
    const txQuery = useQuery({
        queryKey: [TRANSACTION_DETAILS_QUERY_KEY, transactionId],
        queryFn: () => getTransactionDetails(transactionId),
    });

    const transaction = txQuery.data?.transaction;

    const initialSelectedItems = useMemo(
        () => [documentSourceItem],
        [documentSourceItem],
    );

    // EditAllocationDrawer renders this as the SOURCE row (the transaction
    // being allocated). Despite the name, it must be the transaction —
    // the document is the entry in `initialSelectedItems`.
    const txSourceItem = useMemo<MatchableItem | null>(() => {
        if (!transaction) {
            return null;
        }
        return transactionToMatchableItem({ transaction });
    }, [transaction]);

    if (!transaction || !txSourceItem) {
        return null;
    }

    return (
        <AccrualMatchProvider
            sourceTransaction={transaction}
            initialSelectedItems={initialSelectedItems}
            onConfirmSuccess={onSaveSuccess}
        >
            <EditAllocationDrawer
                open
                onClose={onClose}
                sourceItem={txSourceItem}
                onSaveSuccess={onSaveSuccess}
            />
        </AccrualMatchProvider>
    );
};
